/**
 * FpsHealItems.ts — World-space heal/shield pickup system for StormSurge.
 *
 * Items:
 *  • Bandage        — instant +15 HP, cap 75, stack ≤5
 *  • Medkit         — 3 s channel → full 100 HP
 *  • Small Shield   — instant +25 shield, cap 50 (from smalls only)
 *  • Big Pot        — 3 s channel → +50 shield, cap 100
 *
 * Does NOT modify FpsChugSplash.ts.
 */

import * as THREE from 'three';
import { toonMat, createToonGradientMap } from './materials.js';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export type HealKind = 'bandage' | 'medkit' | 'smallShield' | 'bigPot';

export interface HealPickupResult {
  kind: HealKind;
  count: number; // how many the player now holds (after pickup)
}

// ---------------------------------------------------------------------------
// Internal pickup descriptor
// ---------------------------------------------------------------------------

interface PickupDef {
  kind: HealKind;
  color: number;
  emissive: number;
  size: number; // box half-extent (full side length)
  positions: { x: number; z: number }[];
}

interface LivePickup {
  mesh: THREE.Mesh;
  kind: HealKind;
  baseY: number;
  bobPhase: number;
  active: boolean;
  respawnTimer: number; // counts down when inactive
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

const RESPAWN_DELAY = 30; // seconds
const PICKUP_RADIUS = 2;  // metres
const BASE_Y = 0.55;
const BOB_AMP = 0.12;
const BOB_FREQ = 2.4;
const ROT_SPEED = 0.9; // rad/s

const CHANNEL_DURATION_MEDKIT = 3;
const CHANNEL_DURATION_BIGPOT = 3;

const DEFS: PickupDef[] = [
  {
    kind: 'bandage',
    color: 0xffffff,
    emissive: 0xaaaaaa,
    size: 0.32,
    positions: [
      { x: 8, z: -30 },
      { x: -12, z: 5 },
      { x: 25, z: 18 },
    ],
  },
  {
    kind: 'medkit',
    color: 0x22dd55,
    emissive: 0x116622,
    size: 0.42,
    positions: [
      { x: -5, z: -40 },
      { x: 15, z: 30 },
    ],
  },
  {
    kind: 'smallShield',
    color: 0x4488ff,
    emissive: 0x1133aa,
    size: 0.26,
    positions: [
      { x: 20, z: -15 },
      { x: -8, z: 25 },
      { x: 35, z: 5 },
    ],
  },
  {
    kind: 'bigPot',
    color: 0x1155ff,
    emissive: 0x0022aa,
    size: 0.52,
    positions: [
      { x: -25, z: -20 },
      { x: 10, z: -8 },
    ],
  },
];

// ---------------------------------------------------------------------------
// Inventory caps
// ---------------------------------------------------------------------------

const MAX_BANDAGE = 5;
const MAX_MEDKIT = 1;
const MAX_SMALL_SHIELD = 3;
const MAX_BIG_POT = 2;

// ---------------------------------------------------------------------------
// Main class
// ---------------------------------------------------------------------------

export class FpsHealItems {
  // Inventory
  bandageCount = 0;
  medkitCount = 0;
  smallShieldCount = 0;
  bigPotCount = 0;

  // Channel state
  private _isChanneling = false;
  private _channelProgress = 0;
  private _channelDuration = 0;
  private _channelKind: 'medkit' | 'bigPot' | null = null;
  private _onChannelComplete: (() => void) | null = null;

  private readonly pickups: LivePickup[] = [];
  private readonly scene: THREE.Scene;
  private readonly gradient: THREE.DataTexture;

  // DOM
  private readonly hudEl: HTMLDivElement;
  private readonly channelEl: HTMLDivElement;
  private readonly channelFill: HTMLDivElement;
  private readonly channelLabel: HTMLDivElement;
  private readonly channelHint: HTMLDivElement;
  private readonly channelToast: HTMLDivElement;
  private channelToastTimer = 0;

  // Geometry cache per size key
  private readonly geoCache = new Map<number, THREE.BoxGeometry>();

  constructor(scene: THREE.Scene, mount: HTMLElement) {
    this.scene = scene;
    this.gradient = createToonGradientMap();

    // Build world pickups
    let bobIdx = 0;
    for (const def of DEFS) {
      for (const pos of def.positions) {
        const geo = this.getBoxGeo(def.size);
        const mat = toonMat(def.color, this.gradient, {
          emissive: def.emissive,
          emissiveIntensity: 0.55,
        });
        const mesh = new THREE.Mesh(geo, mat);
        mesh.position.set(pos.x, BASE_Y, pos.z);
        mesh.castShadow = true;
        scene.add(mesh);

        this.pickups.push({
          mesh,
          kind: def.kind,
          baseY: BASE_Y,
          bobPhase: bobIdx * 0.73,
          active: true,
          respawnTimer: 0,
        });
        bobIdx++;
      }
    }

    // --- Inventory HUD ---
    this.hudEl = document.createElement('div');
    this.hudEl.id = 'fps-heal-inv';
    Object.assign(this.hudEl.style, {
      position: 'fixed',
      bottom: '80px',
      right: '18px',
      background: 'rgba(0,0,0,0.55)',
      color: '#fff',
      fontFamily: 'monospace',
      fontSize: '13px',
      padding: '8px 12px',
      borderRadius: '6px',
      pointerEvents: 'none',
      userSelect: 'none',
      display: 'none',
      zIndex: '200',
      lineHeight: '1.7',
    });
    mount.appendChild(this.hudEl);

    // --- Channel progress bar ---
    this.channelEl = document.createElement('div');
    this.channelEl.id = 'fps-heal-channel';
    Object.assign(this.channelEl.style, {
      position: 'fixed',
      bottom: '140px',
      left: '50%',
      transform: 'translateX(-50%)',
      width: '220px',
      background: 'linear-gradient(135deg,rgba(10,18,30,0.88),rgba(16,28,46,0.72))',
      border: '1px solid rgba(126,249,168,0.42)',
      boxShadow: '0 10px 30px rgba(0,0,0,0.34),0 0 22px rgba(74,222,128,0.18)',
      borderRadius: '12px',
      padding: '10px 13px 11px',
      display: 'none',
      zIndex: '300',
      pointerEvents: 'none',
      overflow: 'hidden',
    });

    this.channelLabel = document.createElement('div');
    Object.assign(this.channelLabel.style, {
      color: '#fff',
      fontFamily: 'monospace',
      fontSize: '13px',
      fontWeight: '800',
      letterSpacing: '0.04em',
      textAlign: 'center',
      marginBottom: '7px',
      textShadow: '0 0 12px rgba(255,255,255,0.28)',
    });
    this.channelEl.appendChild(this.channelLabel);

    const track = document.createElement('div');
    Object.assign(track.style, {
      width: '100%',
      height: '8px',
      background: 'rgba(255,255,255,0.14)',
      border: '1px solid rgba(255,255,255,0.16)',
      borderRadius: '4px',
      overflow: 'hidden',
    });
    this.channelFill = document.createElement('div');
    Object.assign(this.channelFill.style, {
      height: '100%',
      width: '0%',
      background: '#4ade80',
      borderRadius: '4px',
      boxShadow: '0 0 12px rgba(74,222,128,0.7)',
      transition: 'background 0.1s, width 0.06s linear',
    });
    track.appendChild(this.channelFill);
    this.channelEl.appendChild(track);

    this.channelHint = document.createElement('div');
    Object.assign(this.channelHint.style, {
      color: 'rgba(255,255,255,0.72)',
      fontFamily: 'system-ui, sans-serif',
      fontSize: '10px',
      fontWeight: '700',
      letterSpacing: '0.09em',
      textAlign: 'center',
      marginTop: '7px',
      textTransform: 'uppercase',
    });
    this.channelEl.appendChild(this.channelHint);
    mount.appendChild(this.channelEl);

    this.channelToast = document.createElement('div');
    Object.assign(this.channelToast.style, {
      position: 'fixed',
      bottom: '112px',
      left: '50%',
      transform: 'translateX(-50%) translateY(8px)',
      padding: '6px 10px',
      borderRadius: '999px',
      background: 'rgba(8,14,22,0.82)',
      border: '1px solid rgba(255,255,255,0.22)',
      color: '#fff',
      fontFamily: 'system-ui, sans-serif',
      fontSize: '11px',
      fontWeight: '800',
      letterSpacing: '0.08em',
      opacity: '0',
      zIndex: '299',
      pointerEvents: 'none',
      transition: 'opacity 0.16s ease, transform 0.16s ease',
      textTransform: 'uppercase',
    });
    mount.appendChild(this.channelToast);
  }

  // ---------------------------------------------------------------------------
  // Geometry cache helper
  // ---------------------------------------------------------------------------

  private getBoxGeo(size: number): THREE.BoxGeometry {
    if (!this.geoCache.has(size)) {
      this.geoCache.set(size, new THREE.BoxGeometry(size, size, size));
    }
    return this.geoCache.get(size)!;
  }

  // ---------------------------------------------------------------------------
  // Public getters
  // ---------------------------------------------------------------------------

  get isChanneling(): boolean {
    return this._isChanneling;
  }

  get channelProgress(): number {
    return this._channelProgress;
  }

  // ---------------------------------------------------------------------------
  // Pickup detection
  // ---------------------------------------------------------------------------

  findNearestPickup(
    x: number,
    z: number,
    radius: number,
  ): { kind: HealKind; x: number; z: number; dist: number } | null {
    const r2 = radius * radius;
    let best: { kind: HealKind; x: number; z: number; dist: number } | null = null;
    for (const p of this.pickups) {
      if (!p.active) continue;
      const dx = x - p.mesh.position.x;
      const dz = z - p.mesh.position.z;
      const dist2 = dx * dx + dz * dz;
      if (dist2 > r2) continue;
      const dist = Math.sqrt(dist2);
      if (!best || dist < best.dist) {
        best = { kind: p.kind, x: p.mesh.position.x, z: p.mesh.position.z, dist };
      }
    }
    return best;
  }

  /** Bot looting — consumes world pickup and returns HP restored (no player inventory). */
  botConsumeAt(x: number, z: number, radius: number): number {
    const r2 = radius * radius;
    for (const p of this.pickups) {
      if (!p.active) continue;
      const dx = x - p.mesh.position.x;
      const dz = z - p.mesh.position.z;
      if (dx * dx + dz * dz > r2) continue;

      p.active = false;
      p.respawnTimer = RESPAWN_DELAY;
      p.mesh.visible = false;

      switch (p.kind) {
        case 'bandage':
          return 15;
        case 'medkit':
          return 50;
        case 'smallShield':
          return 10;
        case 'bigPot':
          return 20;
        default: {
          const _exhaustive: never = p.kind;
          return _exhaustive;
        }
      }
    }
    return 0;
  }

  tryPickup(playerPos: THREE.Vector3): HealPickupResult | null {
    for (const p of this.pickups) {
      if (!p.active) continue;
      const dx = playerPos.x - p.mesh.position.x;
      const dz = playerPos.z - p.mesh.position.z;
      if (dx * dx + dz * dz > PICKUP_RADIUS * PICKUP_RADIUS) continue;

      // Check inventory cap
      if (!this.canPickup(p.kind)) continue;

      // Add to inventory
      const newCount = this.addToInventory(p.kind);

      // Deactivate pickup (respawn after delay)
      p.active = false;
      p.respawnTimer = RESPAWN_DELAY;
      p.mesh.visible = false;

      this.refreshHud();
      return { kind: p.kind, count: newCount };
    }
    return null;
  }

  private canPickup(kind: HealKind): boolean {
    switch (kind) {
      case 'bandage':     return this.bandageCount < MAX_BANDAGE;
      case 'medkit':      return this.medkitCount < MAX_MEDKIT;
      case 'smallShield': return this.smallShieldCount < MAX_SMALL_SHIELD;
      case 'bigPot':      return this.bigPotCount < MAX_BIG_POT;
    }
  }

  private addToInventory(kind: HealKind): number {
    switch (kind) {
      case 'bandage':     return ++this.bandageCount;
      case 'medkit':      return ++this.medkitCount;
      case 'smallShield': return ++this.smallShieldCount;
      case 'bigPot':      return ++this.bigPotCount;
    }
  }

  // ---------------------------------------------------------------------------
  // Heal application
  // ---------------------------------------------------------------------------

  /** Instant +15 HP, cap 75. Returns new HP. */
  applyBandage(currentHp: number): number {
    if (this.bandageCount <= 0) {
      this.showChannelToast('No bandages', '#ffc8c8');
      return currentHp;
    }
    if (currentHp >= 75) {
      this.showChannelToast('Bandage cap reached', '#ffe08a');
      return currentHp;
    }
    this.bandageCount--;
    this.refreshHud();
    const nextHp = Math.min(75, currentHp + 15);
    this.showChannelToast('Health recovered', '#7ef9a8');
    return nextHp;
  }

  /**
   * Starts 3 s medkit channel. Cannot move during channel (caller enforces).
   * Pass `onComplete` to receive the healed HP when channel finishes.
   */
  applyMedkit(onComplete: (newHp: number, currentHp: number) => void, currentHp: number): void {
    if (this.medkitCount <= 0) {
      this.showChannelToast('No medkit', '#ffc8c8');
      return;
    }
    if (currentHp >= 100) {
      this.showChannelToast('Health already full', '#ffe08a');
      return;
    }
    if (this._isChanneling) {
      this.showChannelToast('Already healing', '#ffe08a');
      return;
    }
    this.medkitCount--;
    this.refreshHud();
    this._startChannel('medkit', CHANNEL_DURATION_MEDKIT, () => {
      onComplete(100, currentHp);
    });
  }

  /** Instant +25 shield, cap 50 (smalls only). Returns new shield. */
  applySmallShield(currentShield: number): number {
    if (this.smallShieldCount <= 0) {
      this.showChannelToast('No small shields', '#ffc8c8');
      return currentShield;
    }
    if (currentShield >= 50) {
      this.showChannelToast('Mini shield cap reached', '#ffe08a');
      return currentShield;
    }
    this.smallShieldCount--;
    this.refreshHud();
    const nextShield = Math.min(50, currentShield + 25);
    this.showChannelToast('Shield recovered', '#93c5fd');
    return nextShield;
  }

  /**
   * Starts 3 s big pot channel. Returns new shield in onComplete callback.
   */
  applyBigPot(onComplete: (newShield: number, currentShield: number) => void, currentShield: number): void {
    if (this.bigPotCount <= 0) {
      this.showChannelToast('No big pot', '#ffc8c8');
      return;
    }
    if (currentShield >= 100) {
      this.showChannelToast('Shield already full', '#ffe08a');
      return;
    }
    if (this._isChanneling) {
      this.showChannelToast('Already healing', '#ffe08a');
      return;
    }
    this.bigPotCount--;
    this.refreshHud();
    this._startChannel('bigPot', CHANNEL_DURATION_BIGPOT, () => {
      onComplete(Math.min(100, currentShield + 50), currentShield);
    });
  }

  /** Cancel an in-progress channel (e.g. player moved). Does NOT refund item. */
  cancelChannel(): void {
    if (!this._isChanneling) return;
    const label = this._channelKind === 'medkit' ? 'Medkit canceled' : 'Shield canceled';
    this._isChanneling = false;
    this._channelProgress = 0;
    this._channelKind = null;
    this._onChannelComplete = null;
    this.channelEl.style.display = 'none';
    this.showChannelToast(label, '#ff8a8a');
  }

  // ---------------------------------------------------------------------------
  // Channel internals
  // ---------------------------------------------------------------------------

  private _startChannel(kind: 'medkit' | 'bigPot', duration: number, onComplete: () => void): void {
    this._isChanneling = true;
    this._channelProgress = 0;
    this._channelDuration = duration;
    this._channelKind = kind;
    this._onChannelComplete = onComplete;

    this.channelLabel.textContent = kind === 'medkit' ? 'Using Medkit…' : 'Drinking Big Pot…';
    this.channelFill.style.background = kind === 'medkit' ? '#4ade80' : '#60a5fa';
    this.channelFill.style.boxShadow = kind === 'medkit' ? '0 0 12px rgba(74,222,128,0.7)' : '0 0 12px rgba(96,165,250,0.72)';
    this.channelHint.textContent = kind === 'medkit' ? 'Hold steady to restore HP' : 'Hold steady to raise shield';
    this.channelEl.style.borderColor = kind === 'medkit' ? 'rgba(126,249,168,0.48)' : 'rgba(96,165,250,0.52)';
    this.channelEl.style.display = 'block';
  }

  private showChannelToast(text: string, color: string): void {
    this.channelToast.textContent = text;
    this.channelToast.style.color = color;
    this.channelToast.style.opacity = '1';
    this.channelToast.style.transform = 'translateX(-50%) translateY(0)';
    this.channelToastTimer = 0.7;
  }

  showFeedback(text: string, color = '#fff'): void {
    this.showChannelToast(text, color);
  }

  // ---------------------------------------------------------------------------
  // Main update loop
  // ---------------------------------------------------------------------------

  update(dt: number): void {
    const t = performance.now() / 1000;

    if (this.channelToastTimer > 0) {
      this.channelToastTimer = Math.max(0, this.channelToastTimer - dt);
      if (this.channelToastTimer <= 0) {
        this.channelToast.style.opacity = '0';
        this.channelToast.style.transform = 'translateX(-50%) translateY(8px)';
      }
    }

    // Animate active pickups
    for (let i = 0; i < this.pickups.length; i++) {
      const p = this.pickups[i]!;

      // Respawn countdown
      if (!p.active) {
        p.respawnTimer -= dt;
        if (p.respawnTimer <= 0) {
          p.active = true;
          p.mesh.visible = true;
        }
        continue;
      }

      // Bob + rotate
      p.mesh.position.y = p.baseY + Math.sin(t * BOB_FREQ + p.bobPhase) * BOB_AMP;
      p.mesh.rotation.y += ROT_SPEED * dt;
    }

    // Channel progress
    if (this._isChanneling) {
      this._channelProgress = Math.min(1, this._channelProgress + dt / this._channelDuration);
      this.channelFill.style.width = `${(this._channelProgress * 100).toFixed(1)}%`;
      const pulse = 1 + Math.sin(t * 9) * 0.018 + this._channelProgress * 0.025;
      this.channelEl.style.transform = `translateX(-50%) scale(${pulse.toFixed(3)})`;
      this.channelEl.style.opacity = String(0.88 + Math.sin(t * 8) * 0.04 + this._channelProgress * 0.08);

      if (this._channelProgress >= 1) {
        const cb = this._onChannelComplete;
        const label = this._channelKind === 'medkit' ? 'Health recovered' : 'Shield recovered';
        const color = this._channelKind === 'medkit' ? '#7ef9a8' : '#93c5fd';
        this._isChanneling = false;
        this._channelProgress = 0;
        this._channelKind = null;
        this._onChannelComplete = null;
        this.channelEl.style.display = 'none';
        this.channelEl.style.transform = 'translateX(-50%) scale(1)';
        this.channelEl.style.opacity = '1';
        this.showChannelToast(label, color);
        cb?.();
      }
    } else {
      this.channelEl.style.transform = 'translateX(-50%) scale(1)';
      this.channelEl.style.opacity = '1';
    }
  }

  // ---------------------------------------------------------------------------
  // HUD
  // ---------------------------------------------------------------------------

  private refreshHud(): void {
    const total = this.bandageCount + this.medkitCount + this.smallShieldCount + this.bigPotCount;
    if (total === 0) {
      this.hudEl.style.display = 'none';
      return;
    }
    this.hudEl.style.display = 'block';
    this.hudEl.innerHTML = [
      this.bandageCount > 0     ? `🩹 Bandage       ×${this.bandageCount}` : '',
      this.medkitCount > 0      ? `💊 Medkit        ×${this.medkitCount}` : '',
      this.smallShieldCount > 0 ? `🔵 Small Shield  ×${this.smallShieldCount}` : '',
      this.bigPotCount > 0      ? `🛡  Big Pot       ×${this.bigPotCount}` : '',
    ].filter(Boolean).join('<br>');
  }

  // ---------------------------------------------------------------------------
  // Dispose
  // ---------------------------------------------------------------------------

  dispose(): void {
    for (const p of this.pickups) {
      p.mesh.removeFromParent();
      (p.mesh.material as THREE.Material).dispose();
    }
    this.pickups.length = 0;
    for (const geo of this.geoCache.values()) geo.dispose();
    this.geoCache.clear();
    this.gradient.dispose();
    this.hudEl.remove();
    this.channelEl.remove();
    this.channelToast.remove();
  }
}
